import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import { escapeHtml } from './escape-html.ts';

/**
 * The canonical styled CourseCa$h™ brand mark: a `$` set smaller than the run
 * around it in place of the `s`, followed by a superscript ™. A trademark has to
 * look the same everywhere, so the mark carries its own `data-brand-mark` hook
 * and tio-ui styles its metrics off that hook — it renders identically with or
 * without a `.app-rich-text` container, which matters because several render
 * sites have none.
 *
 * `role="img"` plus a plain-text `aria-label` is what stops a screen reader
 * spelling out the stylised glyphs ("Course Ca dollar h trademark"). The label is
 * the brand name, so it is identical in every locale, and it is spelled with a
 * space so it is not itself a BRAND_TOKENS match — otherwise re-running this
 * function over its own output would substitute inside the label.
 *
 * The attributes must stay double-quoted. This markup is mirrored into the
 * `common.tas.course_cash.default` translation key and 65 other translation
 * values, and a translation is an ICU message where `'` is the quoting
 * character: single-quoted attributes lose a quote there and the tag swallows
 * its own `>`. Update both representations together when the brand markup
 * changes.
 */
export const COURSE_CASH_MARK =
  '<span data-brand-mark="course-cash"><span role="img" aria-label="Course Cash">CourseCa<small>$</small>h</span><sup aria-hidden="true">&#x2122;</sup></span>';

/**
 * Plaintext brand tokens to style, ordered longest-first so a match on the fully
 * punctuated form is consumed before the shorter variants can partially match it.
 */
const BRAND_TOKENS = ['CourseCa$h™', 'CourseCa$h', 'CourseCash'];

/**
 * Render arbitrary text with every CourseCa$h™ brand token replaced by the canonical
 * styled mark, returning an htmlSafe SafeString.
 *
 * The whole input is HTML-escaped FIRST, so real/arbitrary program names (e.g.
 * `"A & B <x>"`) stay injection-safe; only the exact plaintext brand tokens are then
 * swapped for trusted markup. Non-brand text is left escaped. Because the mark markup
 * never contains a contiguous brand token, and escaping neutralises any pre-existing
 * markup, feeding this function its own rendered output does not double-mangle.
 */
export function courseCashMark(text: string): SafeString {
  const escaped = escapeHtml(text ?? '');
  // Literal split/join (not replaceAll) so `$` in the replacement is never treated
  // as a regex/replacement-pattern special character.
  const styled = BRAND_TOKENS.reduce(
    (acc, token) => acc.split(token).join(COURSE_CASH_MARK),
    escaped
  );
  return htmlSafe(styled);
}
